import type { TextEffectsTextSectionFields } from '../TextEffects/TextEffectsShared/editor/panels/design/TextEffectsShared.designPanel.types';

export const ViewerType = 'wixui.TextParticles' as const;
export const DataType = 'wixui.TextParticlesData' as const;
export const SkinType = 'wixui.skins.TextParticles' as const;

export const TestIds = {
  viewer: {
    canvas: 'text-particles-canvas',
    text: 'text-particles-text',
    animationState: 'data-animation-state',
  },
} as const;

export const DEFAULT_RADIUS = 2;

// TODO: will move to particle renderer props for user to adjust
export const EFFECT_PARTICLE_MAX_LIFETIME = 1000;
export const EFFECT_PARTICLE_MIN_LIFETIME = 500;
export const MIN_EFFECT_PARTICLES = 3;
export const MAX_EFFECT_PARTICLES = 6;

export const dataHooks = {
  settingsPanel: {
    textInputArea: 'textparticles-text-input-area',
  },
  designPanel: {
    movementSection: 'movement-section',
    particleSpreadSlider: 'particle-spread-slider',
    animationDirectionSetting: 'animation-direction-setting',
    windDirectionSlider: 'wind-direction-slider',
    windSpeedSlider: 'wind-speed-slider',
    turbulenceSlider: 'turbulence-slider',
    triggerAnimationButton: 'trigger-animation-button',
    animationDurationSlider: 'textparticles-animation-duration-slider',
    particleAdjustmentSection: 'particle-adjustment-section',
    animationTypeDropdown: 'animation-type-dropdown',
    root: 'text-particles-design-panel',
    particleSizeInput: 'particle-size-input',
    particleOpacityInput: 'particle-opacity-input',
    particleColorCountInput: 'particle-color-count-input',
    particleColorSwatches: 'particle-color-swatches',
    textColorInput: 'text-color-input',
  },
};

export const translationKeys = {
  designPanel: {
    movementSectionTitle:
      'Inspector-TextParticles.Section-Design.Movement-Section-Title',
    title: 'Inspector-TextParticles.Section-Design.Title',
    previewAnimationLabel:
      'Inspector-TextParticles.Section-Design.Preview-Animation-Label',
    revealDirectionLabel:
      'Inspector-TextParticles.Section-Design.Reveal-Direction-Label',
    windDirectionLabel:
      'Inspector-TextParticles.Section-Design.Wind-Direction-Label',
    windSpeedLabel: 'Inspector-TextParticles.Section-Design.Wind-Speed-Label',
    turbulenceLabel: 'Inspector-TextParticles.Section-Design.Turbulence-Label',
    particleSpreadAreaLabel:
      'Inspector-TextParticles.Section-Design.Particle-Spread-Area-Label',
    durationLabel: 'Inspector-TextParticles.Section-Design.Duration-Label',
    particleAdjustmentSectionTitle:
      'Inspector-TextParticles.Section-Design.Particle-Adjustment-Section-Title',
    animationTypeLabel:
      'Inspector-TextParticles.Section-Design.Animation-Type-Label',
    animationTypeIn: 'Inspector-TextParticles.Section-Design.Animation-Type-In',
    animationTypeOut:
      'Inspector-TextParticles.Section-Design.Animation-Type-Out',
    particleStartSizeLabel:
      'Inspector-TextParticles.Section-Design.Particle-Start-Size-Label',
    particleEndSizeLabel:
      'Inspector-TextParticles.Section-Design.Particle-End-Size-Label',
    particleSizeLabel:
      'Inspector-TextParticles.Section-Design.Particle-Size-Label',
    particleSizeInfo:
      'Inspector-TextParticles.Section-Design.Particle-Size-Info',
    particleOpacityInfo:
      'Inspector-TextParticles.Section-Design.Particle-Opacity-Info',
    particleStartOpacityLabel:
      'Inspector-TextParticles.Section-Design.Particle-Start-Opacity-Label',
    particleEndOpacityLabel:
      'Inspector-TextParticles.Section-Design.Particle-End-Opacity-Label',
    particleOpacityLabel:
      'Inspector-TextParticles.Section-Design.Particle-Opacity-Label',
    particleColorCountLabel:
      'Inspector-TextParticles.Section-Design.Particle-Color-Count-Label',
    colorSectionTitle:
      'Inspector-TextParticles.Section-Design.Color-Section-Title',
    textColorLabel: 'Inspector-TextParticles.Section-Design.Text-Color-Label',
    directionFromLeft:
      'Inspector-TextParticles.Section-Design.Direction-From-Left',
    directionFromRight:
      'Inspector-TextParticles.Section-Design.Direction-From-Right',
    directionFromTop:
      'Inspector-TextParticles.Section-Design.Direction-From-Top',
    directionFromBottom:
      'Inspector-TextParticles.Section-Design.Direction-From-Bottom',
    directionFromTopLeft:
      'Inspector-TextParticles.Section-Design.Direction-From-Top-Left',
    directionFromTopRight:
      'Inspector-TextParticles.Section-Design.Direction-From-Top-Right',
    directionFromBottomLeft:
      'Inspector-TextParticles.Section-Design.Direction-From-Bottom-Left',
    directionFromBottomRight:
      'Inspector-TextParticles.Section-Design.Direction-From-Bottom-Right',
  },
  settingsPanel: {
    textFieldLabel: 'TextParticles.Settings_Text-Field-Label',
    title: 'TextParticles.Settings_Title',
  },
  gfpp: {
    changeText: 'gfpp_MainAction_TextParticles.ChangeText',
    help: 'TextParticles_help_gfpp',
  },
};

export const helpIds = {
  settings: 'cb95e8ac-7a2d-4674-8fbc-98d3c034dc88',
  gfpp: 'cb95e8ac-7a2d-4674-8fbc-98d3c034dc88',
};

export const SKIN_PARAM_RANGES = {
  duration: { min: 0.5, max: 4, step: 0.1 },
  spread: { min: 1, max: 4, step: 0.1 },
  'particle-start-size': { min: 0.1, max: 4, step: 0.1 },
  'particle-end-size': { min: 0.1, max: 4, step: 0.1 },
  'particle-start-opacity': { min: 0, max: 100, step: 1 },
  'particle-end-opacity': { min: 0, max: 100, step: 1 },
  'wind-direction': { min: 0, max: 360, step: 1 },
  'wind-speed': { min: 0, max: 4, step: 0.1 },
  turbulence: { min: 0, max: 3, step: 0.1 },
  'particle-color-count': { min: 1, max: 4, step: 1 },
} as const;

export const TEXT_PARTICLES_TEXT_SECTION_FIELDS: TextEffectsTextSectionFields =
  {
    theme: true,
    fontSize: true,
    fontFamily: true,
    spacing: true,
    textStyles: true,
    textTransform: true,
    headingTag: true,
  };
